import type { ReactNode } from 'react';
import { SitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { SWRConfig } from 'swr';

import type { Dictionary } from 'dictionary';
import type { CustomLayoutServiceData, UserData } from 'models/sitecore';
import { componentBuilder } from 'temp/componentBuilder';
import { I18nProvider } from 'utils/i18n';

import { AuthStatusContextProvider } from './AuthStatusContextProvider';
import { CartContextProvider } from './CartContextProvider';
import { ChatContextProvider } from './ChatContextProvider';
import { GlobalMachinesContextProvider } from './GlobalMachinesContextProvider';
import { GlobalMemberPopoversContextProvider } from './GlobalMemberPopoversContextProvider';
import { GlobalPopoversContextProvider } from './GlobalPopoversContextProvider';
import { InitialQueryValueContextProvider } from './InitialQueryValueContextProvider';
import { PriceContextProvider } from './PriceContextProvider';
import { SelectedStoreContextProvider } from './SelectedStoreContextProvider';
import { SelectedStoreInfoContextProvider } from './SelectedStoreInfoContextProvider';
import { ToastContextProvider } from './ToastContextProvider';
import { UserContextProvider } from './UserContextProvider';
import { WishlistContextProvider } from './WishlistContextProvider';

interface Props {
	children: ReactNode;
	dictionary: Dictionary | undefined;
	layoutData: CustomLayoutServiceData;
	userData: UserData | undefined;
}

export default function GlobalContexts({
	children,
	dictionary,
	layoutData,
	userData,
}: Props) {
	const isEditing = layoutData.sitecore.context.pageEditing;
	return (
		<SWRConfig value={{ loadingTimeout: 10_000 }}>
			<SitecoreContext
				componentFactory={componentBuilder.getComponentFactory({
					isEditing,
				})}
				layoutData={layoutData}
			>
				<I18nProvider dictionary={dictionary}>
					<InitialQueryValueContextProvider>
						<AuthStatusContextProvider>
							<UserContextProvider
								jwtUserInfo={{
									customerType: userData?.customerType,
									loginMethod: userData?.loginMethod,
									customerExtVoyado: userData?.customerExtVoyado ?? undefined,
									customerContactId: userData?.customerContactId ?? undefined,
								}}
							>
								<ToastContextProvider>
									<GlobalPopoversContextProvider>
										<CartContextProvider>
											<GlobalMemberPopoversContextProvider>
												<GlobalMachinesContextProvider>
													<SelectedStoreContextProvider>
														<SelectedStoreInfoContextProvider>
															<WishlistContextProvider>
																<ChatContextProvider>
																	<PriceContextProvider>
																		{children}
																	</PriceContextProvider>
																</ChatContextProvider>
															</WishlistContextProvider>
														</SelectedStoreInfoContextProvider>
													</SelectedStoreContextProvider>
												</GlobalMachinesContextProvider>
											</GlobalMemberPopoversContextProvider>
										</CartContextProvider>
									</GlobalPopoversContextProvider>
								</ToastContextProvider>
							</UserContextProvider>
						</AuthStatusContextProvider>
					</InitialQueryValueContextProvider>
				</I18nProvider>
			</SitecoreContext>
		</SWRConfig>
	);
}
GlobalContexts.displayName = 'GlobalContexts';
